import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell 
} from 'recharts';
import { 
  Upload,
  BarChart3,
  TrendingUp,
  Users,
  Building,
  FileText,
  Trash2,
  RefreshCw,
  ArrowUpRight,
  IndianRupee,
  Table,
  FolderOpen,
  X,
  File,
  ChevronRight,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#f59e0b', '#84CC16', '#0D9488', '#059669', '#10B981', '#34D399', '#6EE7B7'];

const PartnerMFBrokerage = () => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [summary, setSummary] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [amcData, setAmcData] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [growthInsights, setGrowthInsights] = useState(null);
  const [pivotData, setPivotData] = useState(null);
  const [pivotConfig, setPivotConfig] = useState({
    rowField: 'month',
    colField: 'amc_code',
    filterField: '',
    filterValue: ''
  });
  const [uploadReport, setUploadReport] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedGrowthMonth, setSelectedGrowthMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('partnerToken');
    return { Authorization: `Bearer ${token}` };
  };

  // Parse month string "MMM-YY" to Date for sorting
  const parseMonthString = useCallback((monthStr) => {
    if (!monthStr || monthStr === 'Unknown') return new Date(0);
    try {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const [monthPart, yearPart] = monthStr.split('-');
      const monthIndex = monthNames.findIndex(m => m.toLowerCase() === monthPart.toLowerCase());
      if (monthIndex === -1) return new Date(0);
      const year = yearPart.length === 2 ? 2000 + parseInt(yearPart) : parseInt(yearPart);
      return new Date(year, monthIndex, 1);
    } catch {
      return new Date(0);
    }
  }, []);

  // Sort monthly data in descending order (most recent first)
  const sortMonthlyDataDesc = useCallback((data) => {
    return [...data].sort((a, b) => {
      const dateA = parseMonthString(a.month);
      const dateB = parseMonthString(b.month);
      return dateB - dateA; // Descending (most recent first)
    });
  }, [parseMonthString]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [summaryRes, monthlyRes, amcRes, clientRes, growthRes, filesRes] = await Promise.all([
        axios.get(`${API}/partner/brokerage/summary`, { headers }),
        axios.get(`${API}/partner/brokerage/by-month`, { headers }),
        axios.get(`${API}/partner/brokerage/by-amc`, { headers }),
        axios.get(`${API}/partner/brokerage/by-client?limit=10`, { headers }),
        axios.get(`${API}/partner/brokerage/growth-insights`, { headers }),
        axios.get(`${API}/partner/brokerage/files`, { headers })
      ]);
      
      setSummary(summaryRes.data);
      // Apply descending sort on monthly data (most recent first)
      setMonthlyData(sortMonthlyDataDesc(monthlyRes.data));
      setAmcData(amcRes.data);
      setClientData(clientRes.data);
      setGrowthInsights(growthRes.data);
      setUploadedFiles(filesRes.data);
      
      if (growthRes.data.available_months) {
        setAvailableMonths(growthRes.data.available_months);
        if (!selectedGrowthMonth) {
          setSelectedGrowthMonth(growthRes.data.selected_month || '');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedGrowthMonth, sortMonthlyDataDesc]);

  const fetchGrowthInsights = useCallback(async (month) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/partner/brokerage/growth-insights`, {
        headers,
        params: { selected_month: month }
      });
      setGrowthInsights(response.data);
    } catch (error) {
      console.error('Error fetching growth insights:', error);
    }
  }, []);

  const handleMonthChange = (month) => {
    setSelectedGrowthMonth(month);
    fetchGrowthInsights(month);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============================================
  // CLIENT-SIDE EXCEL PARSING (Memory Efficient)
  // ============================================

  // AMC Code Mappings (same as backend)
  const CAMS_AMC_CODES = {
    "IF": "360 ONE Mutual Fund",
    "B": "Aditya Birla Sun Life Mutual Fund",
    "AO": "Angel One Mutual Fund",
    "G": "Bandhan Mutual Fund",
    "CHS": "Choice Mutual Fund",
    "D": "DSP Mutual Fund",
    "FTI": "Franklin Templeton Mutual Fund",
    "H": "HDFC Mutual Fund",
    "HLS": "Helios Mutual Fund",
    "O": "HSBC Mutual Fund",
    "P": "ICICI Prudential Mutual Fund",
    "JIO": "Jio BlackRock Mutual Fund",
    "K": "Kotak Mutual Fund",
    "MM": "Mahindra Manulife Mutual Fund",
    "PLF": "NAVI Mutual Fund",
    "PP": "PPFAS Mutual Fund",
    "L": "SBI Mutual Fund",
    "SH": "Shriram Mutual Fund",
    "T": "Tata Mutual Fund",
    "TMF": "Taurus Mutual Fund",
    "UFI": "Unifi Mutual Fund",
    "UK": "Union Mutual Fund",
    "Y": "WhiteOak Capital Mutual Fund",
    "Z": "Zerodha Fund House",
  };

  const KFINTECH_AMC_CODES = {
    "101": "Canara Robeco Mutual Fund",
    "102": "LIC Mutual Fund",
    "105": "JM Financial Mutual Fund",
    "108": "UTI Mutual Fund",
    "116": "BOI AXA Mutual Fund",
    "117": "Mirae Asset Mutual Fund",
    "118": "Edelweiss Mutual Fund",
    "120": "Invesco Mutual Fund",
    "123": "Quantum Mutual Fund",
    "125": "Groww Mutual Fund",
    "127": "Motilal Oswal Mutual Fund",
    "128": "Axis Mutual Fund",
    "129": "PGIM India Mutual Fund",
    "139": "OldBridge Mutual Fund",
    "152": "ITI Mutual Fund",
    "166": "Quant Mutual Fund",
    "176": "Sundaram Mutual Fund",
    "178": "BNP Paribas Mutual Fund",
    "183": "CapitalMind Mutual Fund",
    "185": "Trust Mutual Fund",
    "187": "NJ Mutual Fund",
    "188": "SAMCO Mutual Fund",
    "189": "Bajaj Finserv Mutual Fund",
    "RMF": "Nippon India Mutual Fund",
  };

  // Map AMC code to full name
  const mapAmcCode = (code) => {
    const codeStr = String(code).trim().toUpperCase();
    return CAMS_AMC_CODES[codeStr] || KFINTECH_AMC_CODES[codeStr] || code;
  };

  // Format date to MMM-YY format (e.g., "Nov-25")
  // Handles Excel serial numbers directly to avoid timezone issues
  const formatMonth = (dateVal) => {
    if (!dateVal) return "Unknown";
    try {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      let year, monthNum;
      
      if (typeof dateVal === 'number') {
        // Excel serial date - convert to UTC date components
        // Excel epoch is 1900-01-01, but Excel incorrectly treats 1900 as leap year
        // Days since Excel epoch (subtract 25569 to get Unix days from 1970)
        const utcDays = dateVal - 25569;
        const utcMs = utcDays * 86400 * 1000;
        const date = new Date(utcMs);
        
        // Use UTC methods to get correct month/year
        year = String(date.getUTCFullYear()).slice(-2);
        monthNum = date.getUTCMonth(); // 0-indexed
        
        console.log('[formatMonth] Excel serial:', dateVal, '-> UTC:', date.toISOString(), '-> Month:', months[monthNum], '-', year);
      } else if (dateVal instanceof Date) {
        // Already a Date object - use UTC
        year = String(dateVal.getUTCFullYear()).slice(-2);
        monthNum = dateVal.getUTCMonth();
      } else {
        // String date - parse and use UTC
        const date = new Date(dateVal);
        year = String(date.getUTCFullYear()).slice(-2);
        monthNum = date.getUTCMonth();
      }
      
      return `${months[monthNum]}-${year}`;
    } catch (e) {
      console.error('[formatMonth] Error:', e);
      return "Unknown";
    }
  };

  // Detect file source from column headers
  const detectFileSource = (headers) => {
    const headerStr = headers.map(h => String(h)).join('|');
    const headerStrUpper = headerStr.toUpperCase();
    
    // Check for exact Kfintech columns first
    if (headerStr.includes('Fund Description') || 
        headerStr.includes('Broker Code') || 
        headerStr.includes('Brokerage (in Rs.)') ||
        headerStr.includes('From Date')) {
      return 'KFINTECH';
    }
    
    // Check for exact CAMS columns
    if (headerStr.includes('AMC_CODE') || 
        headerStr.includes('BROKCODE') ||
        headerStr.includes('INV_NAME') ||
        headerStr.includes('FEE_AMT')) {
      return 'CAMS';
    }
    
    // Fallback to partial matching
    if (headerStrUpper.includes('KFIN') || headerStrUpper.includes('KARVY')) {
      return 'KFINTECH';
    }
    if (headerStrUpper.includes('CAMS') || headerStrUpper.includes('COMPUTER AGE')) {
      return 'CAMS';
    }
    
    return 'UNKNOWN';
  };

  // Parse Excel row to record based on source
  const parseRow = (row, headers, source) => {
    // Get value by exact column name or partial match
    const getValue = (exactNames, partialMatches = []) => {
      // First try exact match
      for (const name of exactNames) {
        const idx = headers.findIndex(h => String(h).trim() === name);
        if (idx !== -1 && row[idx] !== undefined && row[idx] !== null && row[idx] !== '') {
          return row[idx]; // Return raw value for date handling
        }
      }
      // Then try partial match
      for (const partial of partialMatches) {
        const idx = headers.findIndex(h => 
          String(h).toUpperCase().includes(partial.toUpperCase())
        );
        if (idx !== -1 && row[idx] !== undefined && row[idx] !== null && row[idx] !== '') {
          return row[idx]; // Return raw value for date handling
        }
      }
      return '';
    };

    const getStringValue = (exactNames, partialMatches = []) => {
      const val = getValue(exactNames, partialMatches);
      return val ? String(val).trim() : '';
    };

    const getNumericValue = (exactNames, partialMatches = []) => {
      const val = getValue(exactNames, partialMatches);
      const num = parseFloat(String(val).replace(/[^0-9.-]/g, ''));
      return isNaN(num) ? 0 : num;
    };

    if (source === 'CAMS') {
      const rawMonth = getValue(['FEE_FRDT', 'MONTH'], ['PERIOD', 'DATE']);
      const rawAmc = getStringValue(['AMC_CODE'], ['AMC', 'FUND HOUSE']);
      return {
        month: formatMonth(rawMonth),
        investor_name: getStringValue(['INV_NAME'], ['INVESTOR NAME', 'INVESTOR', 'CLIENT NAME', 'NAME']),
        scheme_name: getStringValue(['SHORT_NAME'], ['SCHEME NAME', 'SCHEME', 'FUND NAME']),
        amc_code: mapAmcCode(rawAmc),
        brokerage: getNumericValue(['FEE_AMT'], ['BROKERAGE', 'COMMISSION']),
        transaction_type: getStringValue(['FEE_TYPE'], ['TRANSACTION TYPE', 'TXN TYPE', 'TYPE']),
        folio_no: getStringValue(['FOLIO_NO'], ['FOLIO', 'FOLIO NUMBER']),
        advisor_arn: getStringValue(['SUBBROK'], ['SUB BROKER', 'SUB-BROKER']) || 'Main'
      };
    } else {
      // KFINTECH - exact column names from user's file
      const rawMonth = getValue(['Ending Date', 'To Date'], ['END DATE', 'MONTH', 'PERIOD']);
      const rawAmc = getStringValue(['Fund'], ['AMC CODE', 'AMC', 'FUND HOUSE']);
      const subBroker = getStringValue(['Sub-Broker'], ['SUB BROKER', 'SUB-BROKER']);
      return {
        month: formatMonth(rawMonth),
        investor_name: getStringValue(['Investor Name'], ['INVESTOR', 'CLIENT NAME', 'NAME', 'UNIT HOLDER']),
        scheme_name: getStringValue(['Fund Description'], ['SCHEME NAME', 'SCHEME', 'FUND NAME']),
        amc_code: mapAmcCode(rawAmc),
        brokerage: getNumericValue(['Brokerage (in Rs.)', 'GrossBrokerage'], ['BROKERAGE', 'COMMISSION', 'GROSS COMMISSION']),
        transaction_type: getStringValue(['Transaction Description', 'Brokerage Type'], ['TRANSACTION TYPE', 'TXN TYPE']),
        folio_no: getStringValue(['Account Number'], ['FOLIO NO', 'FOLIO', 'FOLIO NUMBER']),
        advisor_arn: subBroker || 'Main'  // If Sub-Broker is blank, use "Main"
      };
    }
  };

  // Client-side Excel parsing and batch upload
  const uploadWithClientParsing = async (file, headers) => {
    const BATCH_SIZE = 1000; // Records per batch (optimized for speed)
    
    setUploadStatus('Phase 1/4: Reading file...');
    console.log('[CLIENT-PARSE] Starting client-side Excel parsing');
    
    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    setUploadStatus('Phase 2/4: Parsing Excel...');
    console.log('[CLIENT-PARSE] Parsing Excel file');
    
    // Parse Excel - DO NOT use cellDates to avoid timezone issues
    const workbook = XLSX.read(arrayBuffer, { 
      type: 'array',
      cellDates: false,  // Keep dates as Excel serial numbers
      cellNF: false,
      cellText: false
    });
    
    // Get first sheet
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    
    // Convert to JSON array
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
    
    if (jsonData.length < 2) {
      throw new Error('File appears to be empty or has no data rows');
    }
    
    const excelHeaders = jsonData[0];
    const dataRows = jsonData.slice(1).filter(row => row.some(cell => cell !== ''));
    
    console.log(`[CLIENT-PARSE] Found ${dataRows.length} rows, headers:`, excelHeaders.slice(0, 5));
    
    // Detect source
    const source = detectFileSource(excelHeaders);
    if (source === 'UNKNOWN') {
      throw new Error('Could not detect file source. Please ensure this is a valid CAMS or Kfintech file.');
    }
    console.log(`[CLIENT-PARSE] Detected source: ${source}`);
    
    // Generate unique upload session ID for this upload
    const uploadSessionId = `upload_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    console.log(`[CLIENT-PARSE] Upload session ID: ${uploadSessionId}`);
    
    // Parse all rows to records
    setUploadStatus('Phase 3/4: Processing records...');
    const allRecords = dataRows
      .map(row => parseRow(row, excelHeaders, source))
      .filter(rec => rec.investor_name && rec.brokerage > 0);
    
    console.log(`[CLIENT-PARSE] Parsed ${allRecords.length} valid records`);
    
    if (allRecords.length === 0) {
      throw new Error('No valid records found. Check that the file has investor names and brokerage amounts.');
    }
    
    // Split into batches
    const batches = [];
    for (let i = 0; i < allRecords.length; i += BATCH_SIZE) {
      batches.push(allRecords.slice(i, i + BATCH_SIZE));
    }
    
    console.log(`[CLIENT-PARSE] Split into ${batches.length} batches of ${BATCH_SIZE} records each`);
    
    // Upload batches
    setUploadStatus('Phase 4/4: Uploading batches...');
    let totalProcessed = 0;
    let totalDuplicates = 0;
    let totalBrokerage = 0;
    let consecutiveErrors = 0;
    
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      
      setUploadStatus(`Phase 4/4: Uploading batch ${i + 1}/${batches.length}...`);
      setUploadProgress(Math.round(((i + 1) / batches.length) * 100));
      
      // Retry logic for each batch
      let retries = 3;
      let success = false;
      
      while (retries > 0 && !success) {
        try {
          const response = await axios.post(`${API}/partner/brokerage/upload/batch`, {
            filename: file.name,
            source: source,
            batch_index: i,
            total_batches: batches.length,
            upload_session_id: uploadSessionId,  // Track this upload session
            records: batch
          }, {
            headers,
            timeout: 90000 // 90 seconds per batch
          });
          
          totalProcessed += response.data.records_processed;
          totalDuplicates += response.data.duplicates;
          totalBrokerage += response.data.batch_brokerage;
          
          console.log(`[CLIENT-PARSE] Batch ${i + 1}/${batches.length} complete: ${response.data.records_processed} records`);
          success = true;
          consecutiveErrors = 0;
          
          // Small delay between batches to prevent server overload
          if (i < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        } catch (error) {
          retries--;
          consecutiveErrors++;
          console.error(`[CLIENT-PARSE] Batch ${i + 1} attempt failed (${3 - retries}/3):`, error.message);
          
          if (retries > 0) {
            // Wait before retry with exponential backoff
            await new Promise(resolve => setTimeout(resolve, 1000 * (4 - retries)));
          } else {
            throw new Error(`Batch ${i + 1} failed after 3 retries: ${error.response?.data?.detail || error.message}`);
          }
        }
      }
    }
    
    return {
      data: {
        success: true,
        file_name: file.name,
        source: source,
        total_entries: totalProcessed,
        new_entries: totalProcessed - totalDuplicates,
        duplicates: totalDuplicates,
        errors: 0,
        total_brokerage: totalBrokerage
      }
    };
  };

  // Helper: Upload a single chunk with retry logic (fallback method)
  const uploadChunkWithRetry = async (url, formData, headers, maxRetries = 3) => {
    let lastError;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await axios.post(url, formData, {
          headers,
          timeout: 90000 // 90 seconds per chunk
        });
        return true;
      } catch (error) {
        lastError = error;
        console.log(`Chunk upload attempt ${attempt}/${maxRetries} failed:`, error.message);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
        }
      }
    }
    throw lastError;
  };

  // Chunked upload for large files (>5MB) with clear phase separation
  const uploadFileChunked = async (file, headers, startProcessingProgress) => {
    const CHUNK_SIZE = 1 * 1024 * 1024; // 1MB chunks (smaller for reliability)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    // ========== PHASE 1: INITIALIZE ==========
    setUploadStatus('Phase 1/3: Initializing...');
    console.log(`[UPLOAD] Phase 1: Initializing upload for ${file.name} (${(file.size/1024/1024).toFixed(2)}MB, ${totalChunks} chunks)`);
    
    let upload_id;
    try {
      const initFormData = new FormData();
      initFormData.append('filename', file.name);
      initFormData.append('total_chunks', totalChunks.toString());
      initFormData.append('file_size', file.size.toString());
      
      const initResponse = await axios.post(`${API}/partner/brokerage/upload/init`, initFormData, {
        headers,
        timeout: 30000
      });
      
      upload_id = initResponse.data.upload_id;
      console.log(`[UPLOAD] Phase 1 complete: upload_id=${upload_id}`);
    } catch (error) {
      console.error('[UPLOAD] Phase 1 FAILED:', error);
      throw new Error(`Initialization failed: ${error.response?.data?.detail || error.message}`);
    }
    
    // ========== PHASE 2: UPLOAD CHUNKS ==========
    setUploadStatus('Phase 2/3: Uploading...');
    console.log(`[UPLOAD] Phase 2: Uploading ${totalChunks} chunks...`);
    
    try {
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const chunkFormData = new FormData();
        chunkFormData.append('upload_id', upload_id);
        chunkFormData.append('chunk_index', i.toString());
        chunkFormData.append('chunk', chunk);
        
        await uploadChunkWithRetry(`${API}/partner/brokerage/upload/chunk`, chunkFormData, headers);
        
        // Update progress
        const progress = Math.round(((i + 1) / totalChunks) * 100);
        setUploadProgress(progress);
        setUploadStatus(`Phase 2/3: Uploading... (${i + 1}/${totalChunks} chunks)`);
      }
      console.log(`[UPLOAD] Phase 2 complete: All ${totalChunks} chunks uploaded`);
    } catch (error) {
      console.error('[UPLOAD] Phase 2 FAILED:', error);
      throw new Error(`Upload failed at chunk: ${error.response?.data?.detail || error.message}`);
    }
    
    // ========== PHASE 3: PROCESS ==========
    setUploadStatus('Phase 3/3: Processing...');
    setUploadProgress(100);
    startProcessingProgress();
    console.log('[UPLOAD] Phase 3: Starting server-side processing...');
    
    try {
      const completeFormData = new FormData();
      completeFormData.append('upload_id', upload_id);
      
      // Trigger async processing
      await axios.post(`${API}/partner/brokerage/upload/complete`, completeFormData, {
        headers,
        timeout: 30000
      });
      console.log('[UPLOAD] Phase 3: Processing started, polling for completion...');
    } catch (error) {
      console.error('[UPLOAD] Phase 3 start FAILED:', error);
      throw new Error(`Processing start failed: ${error.response?.data?.detail || error.message}`);
    }
    
    // ========== PHASE 3b: POLL FOR COMPLETION ==========
    const pollForStatus = async () => {
      const maxAttempts = 180; // 3 minutes max (180 * 1 second)
      let attempts = 0;
      let consecutiveErrors = 0;
      
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        try {
          const statusResponse = await axios.get(`${API}/partner/brokerage/upload/status/${upload_id}`, {
            headers,
            timeout: 15000
          });
          
          consecutiveErrors = 0; // Reset error counter on success
          const { status, result, error } = statusResponse.data;
          
          if (status === 'completed') {
            console.log('[UPLOAD] Phase 3 complete: Processing finished successfully');
            return { data: result };
          } else if (status === 'failed') {
            console.error('[UPLOAD] Phase 3 FAILED:', error);
            throw new Error(`Processing failed: ${error || 'Unknown error'}`);
          }
          
          // Update status message
          setUploadStatus(`Phase 3/3: Processing... (${attempts}s)`);
        } catch (pollError) {
          consecutiveErrors++;
          console.log(`[UPLOAD] Poll error (${consecutiveErrors}):`, pollError.message);
          
          if (pollError.response?.status === 404) {
            throw new Error('Upload session expired. Please try again.');
          }
          
          // Allow up to 5 consecutive poll errors before giving up
          if (consecutiveErrors >= 5) {
            throw new Error('Connection lost during processing. Please check your uploads.');
          }
        }
        
        attempts++;
      }
      
      throw new Error('Processing timeout (3 min). Please check uploads and try again if needed.');
    };
    
    return await pollForStatus();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setProcessingProgress(0);
    setUploadStatus('Cleaning up previous sessions...');
    setUploadReport(null);

    const headers = getAuthHeaders();
    
    // Auto-cleanup before upload to clear any stale sessions
    await silentCleanup();
    
    // Check file extension
    const isExcelFile = file.name.toLowerCase().endsWith('.xls') || file.name.toLowerCase().endsWith('.xlsx');
    
    // Use client-side parsing for Excel files (most memory efficient)
    const useClientParsing = isExcelFile && file.size > 1 * 1024 * 1024; // > 1MB

    let processingInterval = null;
    const startProcessingProgress = () => {
      setProcessingProgress(0);
      let progress = 0;
      processingInterval = setInterval(() => {
        const increment = progress < 30 ? 8 : progress < 60 ? 4 : progress < 80 ? 2 : 0.5;
        progress = Math.min(progress + increment, 90);
        setProcessingProgress(Math.round(progress));
      }, 500);
    };

    try {
      let response;
      
      if (useClientParsing) {
        // CLIENT-SIDE PARSING: Most memory efficient for large files
        console.log(`[UPLOAD] Using CLIENT-SIDE parsing for ${(file.size / 1024 / 1024).toFixed(2)}MB file`);
        response = await uploadWithClientParsing(file, headers);
        setProcessingProgress(100);
      } else if (file.size > 5 * 1024 * 1024) {
        // Large file: Use chunked upload (fallback)
        console.log(`Using chunked upload for ${(file.size / 1024 / 1024).toFixed(2)}MB file`);
        setUploadStatus('Phase 1/3: Initializing...');
        response = await uploadFileChunked(file, headers, startProcessingProgress);
        if (processingInterval) clearInterval(processingInterval);
        setProcessingProgress(100);
      } else {
        // Small file: Use regular upload
        setUploadStatus('Phase 2/3: Uploading...');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('source', 'auto');
        
        response = await axios.post(`${API}/partner/brokerage/upload`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
            setUploadStatus(`Phase 2/3: Uploading... (${percentCompleted}%)`);
            if (percentCompleted === 100) {
              setUploadStatus('Phase 3/3: Processing...');
              startProcessingProgress();
            }
          }
        });
        
        if (processingInterval) clearInterval(processingInterval);
        setProcessingProgress(100);
      }
      
      setUploadReport(response.data);
      toast.success(`Successfully uploaded ${response.data.new_entries} records!`);
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Failed to upload file';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out. Please try again.';
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Maximum upload size exceeded.';
      } else if (error.response?.status === 504 || error.response?.status === 502) {
        errorMessage = 'Server timeout. Please try again.';
      }
      toast.error(errorMessage);
      if (processingInterval) clearInterval(processingInterval);
    } finally {
      // Auto-cleanup after upload (success or failure)
      await silentCleanup();
      
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setProcessingProgress(0);
        setUploadStatus('');
      }, 500);
      e.target.value = '';
    }
  };

  const fetchPivotData = async () => {
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams({
        row_field: pivotConfig.rowField,
        col_field: pivotConfig.colField
      });
      if (pivotConfig.filterField && pivotConfig.filterValue) {
        params.append('filter_field', pivotConfig.filterField);
        params.append('filter_value', pivotConfig.filterValue);
      }
      
      const response = await axios.get(`${API}/partner/brokerage/pivot?${params}`, { headers });
      setPivotData(response.data);
    } catch (error) {
      console.error('Error fetching pivot data:', error);
      toast.error('Failed to generate pivot table');
    }
  };

  // Silent cleanup - runs automatically, no toast notifications
  const silentCleanup = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.delete(`${API}/partner/brokerage/upload/cleanup`, { headers });
      const { sessions_deleted, chunks_deleted } = response.data;
      if (sessions_deleted > 0 || chunks_deleted > 0) {
        console.log(`[CLEANUP] Auto-cleaned ${sessions_deleted} sessions, ${chunks_deleted} chunks`);
      }
    } catch (error) {
      console.log('[CLEANUP] Auto-cleanup failed (non-critical):', error.message);
    }
  };

  const handleCleanupUploads = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.delete(`${API}/partner/brokerage/upload/cleanup`, { headers });
      const { sessions_deleted, chunks_deleted } = response.data;
      if (sessions_deleted > 0 || chunks_deleted > 0) {
        toast.success(`Cleaned up ${sessions_deleted} stale sessions and ${chunks_deleted} orphaned chunks`);
      } else {
        toast.info('No stale uploads to clean up');
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      toast.error('Failed to cleanup uploads');
    }
  };

  const handleClearData = async () => {
    if (!window.confirm('Are you sure you want to clear all brokerage data? This cannot be undone.')) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/partner/brokerage/clear`, { headers });
      toast.success('All data cleared');
      fetchData();
      setPivotData(null);
      setUploadReport(null);
    } catch (error) {
      toast.error('Failed to clear data');
    }
  };

  const handleDeleteFile = async (fileName) => {
    if (!window.confirm(`Delete all records from "${fileName}"?`)) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      const response = await axios.delete(`${API}/partner/brokerage/files/${encodeURIComponent(fileName)}`, { headers });
      toast.success(`Deleted ${response.data.deleted_count} records from ${fileName}`);
      fetchData();
      setPivotData(null);
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Excel download handlers
  const handleDownloadExcel = async (type) => {
    try {
      const headers = getAuthHeaders();
      let url = `${API}/partner/brokerage/download/${type}`;
      
      // Add pivot params if downloading pivot
      if (type === 'pivot') {
        url += `?row_field=${pivotConfig.rowField}&col_field=${pivotConfig.colField}`;
      }
      
      const response = await axios.get(url, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${type}_brokerage.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Excel downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Excel file');
    }
  };

  if (loading && !summary) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="mf-brokerage-page" className="w-full max-w-full overflow-hidden">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">MF Brokerage Analytics</h1>
        <p className="text-slate-400 mt-1">Upload and analyze your mutual fund brokerage data</p>
      </div>

      <div className="flex gap-6 overflow-hidden">
        {/* Files Sidebar */}
        {sidebarOpen && (
          <aside className="w-64 flex-shrink-0 hidden lg:block">
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sticky top-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-amber-400" />
                  Uploaded Files
                </h3>
                <button 
                  onClick={() => setSidebarOpen(false)}
                  className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {uploadedFiles.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <File className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No files uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {uploadedFiles.map((file, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-amber-500/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-white truncate" title={file.file_name}>
                            {file.file_name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${file.source === 'CAMS' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                              {file.source}
                            </span>
                            <span className="text-xs text-slate-500">
                              {file.new_records} records
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            ₹{file.total_brokerage.toLocaleString('en-IN')}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteFile(file.file_name)}
                          className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          title="Delete file"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-xs text-slate-500 text-center">
                  {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} • {summary?.total_records?.toLocaleString() || 0} total records
                </p>
              </div>
            </div>
          </aside>
        )}

        {/* Sidebar Toggle (when closed) */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="fixed left-64 top-1/2 -translate-y-1/2 bg-amber-500 text-slate-900 p-2 rounded-r-lg shadow-lg hover:bg-amber-400 transition-colors z-10"
            title="Show files"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {/* Upload Section */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="font-bold text-lg sm:text-xl text-white">Upload Brokerage Files</h2>
                <p className="text-slate-400 text-sm mt-1">Upload CAMS or Kfintech Excel files (.xlsx, .xls)</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                <Label htmlFor="file-upload" className="cursor-pointer">
                  <div className={`flex items-center gap-2 bg-amber-500 text-slate-900 px-4 sm:px-6 py-2 sm:py-3 rounded-full hover:bg-amber-400 transition-all font-semibold text-sm sm:text-base ${uploading ? 'opacity-70 cursor-not-allowed' : ''}`}>
                    <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>{uploading ? 'Uploading...' : 'Upload'}</span>
                  </div>
                  <Input
                    id="file-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    data-testid="file-upload-input"
                  />
                </Label>
                <Button 
                  variant="outline" 
                  onClick={fetchData}
                  disabled={uploading}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleCleanupUploads}
                  disabled={uploading}
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  data-testid="cleanup-btn"
                  title="Clean up stale uploads"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  <span className="text-xs">Cleanup</span>
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleClearData}
                  disabled={uploading}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  data-testid="clear-data-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="mt-4" data-testid="upload-progress">
                {/* Phase indicator - shows current phase */}
                {uploadStatus && (
                  <div className="mb-3 px-3 py-2 bg-slate-800 rounded-lg border border-slate-700">
                    <div className="flex items-center gap-2">
                      <div className="animate-pulse w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-sm font-mono text-slate-300">{uploadStatus}</span>
                    </div>
                  </div>
                )}
                
                {/* Upload progress bar - show during Phase 2 */}
                {uploadStatus.includes('Uploading') && (
                  <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Upload className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-medium text-amber-300">Uploading chunks...</span>
                      </div>
                      <span className="text-sm font-medium text-amber-400">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}
                
                {/* Processing progress bar - show during Phase 3 */}
                {uploadStatus.includes('Processing') && (
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-400 border-t-transparent"></div>
                        <span className="text-sm font-medium text-blue-300">Server processing...</span>
                      </div>
                      <span className="text-sm font-medium text-blue-400">{processingProgress}%</span>
                    </div>
                    <div className="w-full bg-blue-500/20 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${processingProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-blue-400 mt-2">Parsing records, checking duplicates, and saving to database</p>
                  </div>
                )}
                
                {/* Initializing indicator - show during Phase 1 */}
                {uploadStatus.includes('Initializing') && (
                  <div className="p-4 bg-slate-500/10 rounded-lg border border-slate-500/30">
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent"></div>
                      <span className="text-sm font-medium text-slate-300">Preparing upload...</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upload Report */}
            {uploadReport && (
              <div className="mt-6 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30" data-testid="upload-report">
                <h3 className="font-semibold text-emerald-400 mb-2">Upload Report: {uploadReport.file_name}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Source:</span>
                    <span className="ml-2 font-medium text-white">{uploadReport.source}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Total:</span>
                    <span className="ml-2 font-medium text-white">{uploadReport.total_entries}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">New:</span>
                    <span className="ml-2 font-medium text-emerald-400">{uploadReport.new_entries}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Duplicates:</span>
                    <span className="ml-2 font-medium text-amber-400">{uploadReport.duplicates}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Errors:</span>
                    <span className="ml-2 font-medium text-red-400">{uploadReport.errors}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4" data-testid="total-brokerage-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">Total Brokerage</span>
                <IndianRupee className="w-4 h-4 text-amber-400 flex-shrink-0" />
              </div>
              <div className="text-lg sm:text-2xl font-bold text-white truncate">
                {formatCurrency(summary?.total_brokerage || 0)}
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">Total Records</span>
                <FileText className="w-4 h-4 text-amber-400 flex-shrink-0" />
              </div>
              <div className="text-lg sm:text-2xl font-bold text-white">
                {summary?.total_records?.toLocaleString() || 0}
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">Unique Clients</span>
                <Users className="w-4 h-4 text-amber-400 flex-shrink-0" />
              </div>
              <div className="text-lg sm:text-2xl font-bold text-white">
                {summary?.unique_clients?.toLocaleString() || 0}
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">AMCs</span>
                <Building className="w-4 h-4 text-amber-400 flex-shrink-0" />
              </div>
              <div className="text-lg sm:text-2xl font-bold text-white">
                {summary?.unique_amcs || 0}
              </div>
            </div>
          </div>

          {/* Growth Insights */}
          {growthInsights && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <h3 className="font-bold text-base sm:text-lg text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                  Year-over-Year Growth
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs sm:text-sm text-slate-400">Compare:</span>
                  <Select value={selectedGrowthMonth} onValueChange={handleMonthChange}>
                    <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white text-sm">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {availableMonths.map(month => (
                        <SelectItem key={month} value={month} className="text-white hover:bg-slate-700">{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {growthInsights.year_ago_month && (
                    <span className="text-xs text-slate-500">vs {growthInsights.year_ago_month}</span>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Fastest Growing Client */}
                <div className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl p-3 sm:p-4 text-white overflow-hidden" data-testid="fastest-client">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 flex-shrink-0" />
                    <span className="text-amber-200 text-xs">Fastest Growing Client</span>
                  </div>
                  {growthInsights.fastest_growing_client ? (
                    <>
                      <div className="font-bold text-sm truncate" title={growthInsights.fastest_growing_client.name}>
                        {growthInsights.fastest_growing_client.name}
                      </div>
                      {growthInsights.fastest_growing_client.data_available ? (
                        <div className="flex items-center gap-1 mt-1 text-lime-300 flex-wrap">
                          <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
                          <span className="font-semibold text-xs">{growthInsights.fastest_growing_client.growth_percent}%</span>
                        </div>
                      ) : (
                        <div className="mt-1 text-amber-200 text-xs">
                          No YoY data
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-amber-200 text-xs">No data</div>
                  )}
                </div>

                {/* Fastest Growing AMC */}
                <div className="bg-gradient-to-br from-amber-700 to-amber-800 rounded-xl p-3 sm:p-4 text-white overflow-hidden" data-testid="fastest-amc">
                  <div className="flex items-center gap-2 mb-1">
                    <Building className="w-4 h-4 flex-shrink-0" />
                    <span className="text-amber-200 text-xs">Fastest Growing AMC</span>
                  </div>
                  {growthInsights.fastest_growing_amc ? (
                    <>
                      <div className="font-bold text-sm truncate">{growthInsights.fastest_growing_amc.name}</div>
                      {growthInsights.fastest_growing_amc.data_available ? (
                        <div className="flex items-center gap-1 mt-1 text-lime-300 flex-wrap">
                          <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
                          <span className="font-semibold text-xs">{growthInsights.fastest_growing_amc.growth_percent}%</span>
                        </div>
                      ) : (
                        <div className="mt-1 text-amber-200 text-xs">No YoY data</div>
                      )}
                    </>
                  ) : (
                    <div className="text-amber-200 text-xs">No data</div>
                  )}
                </div>

                {/* Fastest Growing Scheme */}
                <div className="bg-gradient-to-br from-amber-800 to-amber-900 rounded-xl p-3 sm:p-4 text-white overflow-hidden" data-testid="fastest-scheme">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 flex-shrink-0" />
                    <span className="text-amber-200 text-xs">Fastest Growing Scheme</span>
                  </div>
                  {growthInsights.fastest_growing_scheme ? (
                    <>
                      <div className="font-bold text-sm truncate" title={growthInsights.fastest_growing_scheme.name}>
                        {growthInsights.fastest_growing_scheme.name}
                      </div>
                      {growthInsights.fastest_growing_scheme.data_available ? (
                        <div className="flex items-center gap-1 mt-1 text-lime-300 flex-wrap">
                          <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
                          <span className="font-semibold text-xs">{growthInsights.fastest_growing_scheme.growth_percent}%</span>
                        </div>
                      ) : (
                        <div className="mt-1 text-amber-200 text-xs">No YoY data</div>
                      )}
                    </>
                  ) : (
                    <div className="text-amber-200 text-sm">No data</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tabs for Charts */}
          <Tabs defaultValue="monthly" className="mb-6 w-full overflow-hidden">
            <TabsList className="mb-4 bg-slate-800 border border-slate-700 flex-wrap h-auto p-1">
              <TabsTrigger value="monthly" data-testid="tab-monthly" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-xs sm:text-sm">
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Monthly
              </TabsTrigger>
              <TabsTrigger value="amc" data-testid="tab-amc" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-xs sm:text-sm">
                <Building className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                AMC
              </TabsTrigger>
              <TabsTrigger value="clients" data-testid="tab-clients" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-xs sm:text-sm">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Clients
              </TabsTrigger>
              <TabsTrigger value="pivot" data-testid="tab-pivot" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-xs sm:text-sm">
                <Table className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Analytics
              </TabsTrigger>
            </TabsList>

            <TabsContent value="monthly">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6 overflow-hidden">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-base sm:text-lg text-white">Monthly Brokerage Revenue</h3>
                  {monthlyData.length > 0 && (
                    <Button 
                      onClick={() => handleDownloadExcel('monthly')} 
                      variant="outline" 
                      size="sm"
                      className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
                      data-testid="download-monthly-excel"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Excel
                    </Button>
                  )}
                </div>
                {monthlyData.length > 0 ? (
                  <div className="w-full overflow-x-auto">
                    <div className="min-w-[500px]">
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                          <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                          <Tooltip 
                        formatter={(value) => [formatCurrency(value), 'Brokerage']}
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                        labelStyle={{ color: '#f1f5f9' }}
                      />
                      <Bar dataKey="total_brokerage" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No data available. Upload brokerage files to see analytics.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="amc">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6 overflow-hidden">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-base sm:text-lg text-white">Brokerage by AMC</h3>
                  {amcData.length > 0 && (
                    <Button 
                      onClick={() => handleDownloadExcel('amc')} 
                      variant="outline" 
                      size="sm"
                      className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
                      data-testid="download-amc-excel"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Excel
                    </Button>
                  )}
                </div>
                {amcData.length > 0 ? (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <div className="w-full overflow-x-auto">
                      <div className="min-w-[300px]">
                        <ResponsiveContainer width="100%" height={280}>
                          <PieChart>
                            <Pie
                              data={amcData.slice(0, 7)}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="total_brokerage"
                              nameKey="amc_code"
                            >
                              {amcData.slice(0, 7).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="overflow-auto max-h-[280px]">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-800">
                          <tr>
                            <th className="text-left p-2 text-slate-400">AMC</th>
                            <th className="text-right p-2 text-slate-400">Brokerage</th>
                            <th className="text-right p-2 text-slate-400">Records</th>
                          </tr>
                        </thead>
                        <tbody>
                          {amcData.map((amc, index) => (
                            <tr key={index} className="border-t border-slate-700">
                              <td className="p-2 text-white font-medium truncate max-w-[150px]" title={amc.amc_code}>{amc.amc_code}</td>
                              <td className="p-2 text-right text-slate-300 whitespace-nowrap">{formatCurrency(amc.total_brokerage)}</td>
                              <td className="p-2 text-right text-slate-400">{amc.record_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Building className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No data available</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="clients">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6 overflow-hidden">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-base sm:text-lg text-white">Top 10 Clients by Brokerage</h3>
                  {clientData.length > 0 && (
                    <Button 
                      onClick={() => handleDownloadExcel('clients')} 
                      variant="outline" 
                      size="sm"
                      className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
                      data-testid="download-clients-excel"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Excel
                    </Button>
                  )}
                </div>
                {clientData.length > 0 ? (
                  <div className="w-full overflow-x-auto">
                    <div className="min-w-[400px]">
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={clientData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis type="number" tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                          <YAxis 
                            type="category" 
                            dataKey="client_name" 
                            width={120}
                            tick={{ fontSize: 10, fill: '#94a3b8' }}
                            tickFormatter={(v) => v.length > 15 ? v.substring(0, 15) + '...' : v}
                          />
                          <Tooltip 
                            formatter={(value) => [formatCurrency(value), 'Brokerage']}
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                          />
                          <Bar dataKey="total_brokerage" fill="#84CC16" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No data available</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="pivot">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-base sm:text-lg text-white">Analytics Table</h3>
                  {pivotData && (
                    <Button 
                      onClick={() => handleDownloadExcel('pivot')} 
                      variant="outline" 
                      size="sm"
                      className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
                      data-testid="download-pivot-excel"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Excel
                    </Button>
                  )}
                </div>
                
                {/* Pivot Configuration */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Rows</Label>
                    <Select value={pivotConfig.rowField} onValueChange={(v) => setPivotConfig(p => ({...p, rowField: v}))}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-sm" data-testid="pivot-row-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="month" className="text-white">Month</SelectItem>
                        <SelectItem value="amc_code" className="text-white">AMC</SelectItem>
                        <SelectItem value="client_name" className="text-white">Client</SelectItem>
                        <SelectItem value="scheme_name" className="text-white">Scheme</SelectItem>
                        <SelectItem value="advisor_arn" className="text-white">Advisor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Columns</Label>
                    <Select value={pivotConfig.colField} onValueChange={(v) => setPivotConfig(p => ({...p, colField: v}))}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-sm" data-testid="pivot-col-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="month" className="text-white">Month</SelectItem>
                        <SelectItem value="amc_code" className="text-white">AMC</SelectItem>
                        <SelectItem value="client_name" className="text-white">Client</SelectItem>
                        <SelectItem value="scheme_name" className="text-white">Scheme</SelectItem>
                        <SelectItem value="advisor_arn" className="text-white">Advisor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Filter By</Label>
                    <Select value={pivotConfig.filterField || "none"} onValueChange={(v) => setPivotConfig(p => ({...p, filterField: v === "none" ? "" : v}))}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-sm">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="none" className="text-white">None</SelectItem>
                        <SelectItem value="amc_code" className="text-white">AMC</SelectItem>
                        <SelectItem value="month" className="text-white">Month</SelectItem>
                        <SelectItem value="advisor_arn" className="text-white">Advisor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Filter Value</Label>
                    <Input 
                      value={pivotConfig.filterValue}
                      onChange={(e) => setPivotConfig(p => ({...p, filterValue: e.target.value}))}
                      placeholder="Value"
                      className="bg-slate-800 border-slate-700 text-white text-sm"
                      data-testid="pivot-filter-input"
                    />
                  </div>
                  <div className="flex items-end col-span-2 sm:col-span-1">
                    <Button onClick={fetchPivotData} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold text-sm" data-testid="generate-pivot-btn">
                      Generate
                    </Button>
                  </div>
                </div>

                {/* Pivot Table Display */}
                {pivotData ? (
                  <div className="relative w-full" style={{ maxWidth: '100%' }}>
                    <div 
                      className="border border-slate-700 rounded-lg"
                      style={{ 
                        maxHeight: '400px',
                        overflowX: 'scroll',
                        overflowY: 'auto',
                        display: 'block'
                      }}
                    >
                      <table className="border-collapse text-sm" style={{ width: 'max-content' }}>
                        <thead className="sticky top-0 z-20">
                          <tr>
                            <th className="border border-slate-700 px-3 py-2 text-left font-semibold text-amber-400 bg-slate-800 sticky left-0 z-30 whitespace-nowrap">
                              {pivotData.row_field}
                            </th>
                            {pivotData.columns.map(col => (
                              <th key={col} className="border border-slate-700 px-3 py-2 text-right font-semibold text-slate-300 bg-slate-800 whitespace-nowrap">
                                {col}
                              </th>
                            ))}
                            <th className="border border-slate-700 px-3 py-2 text-right font-semibold text-amber-400 bg-slate-800 whitespace-nowrap">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {pivotData.rows.map(row => {
                            const rowTotal = pivotData.columns.reduce((sum, col) => 
                              sum + (pivotData.data[row]?.[col] || 0), 0);
                            return (
                              <tr key={row}>
                                <td className="border border-slate-700 px-3 py-2 font-medium text-white bg-slate-900 sticky left-0 z-10 whitespace-nowrap">
                                  {row}
                                </td>
                                {pivotData.columns.map(col => (
                                  <td key={col} className="border border-slate-700 px-3 py-2 text-right text-slate-300 whitespace-nowrap">
                                    {pivotData.data[row]?.[col] ? formatCurrency(pivotData.data[row][col]) : '-'}
                                  </td>
                                ))}
                                <td className="border border-slate-700 px-3 py-2 text-right font-semibold text-amber-400 bg-slate-800/30 whitespace-nowrap">
                                  {formatCurrency(rowTotal)}
                                </td>
                              </tr>
                            );
                          })}
                          {/* Column Totals */}
                          <tr className="bg-slate-800 sticky bottom-0 z-10">
                            <td className="border border-slate-700 px-3 py-2 font-bold text-amber-400 sticky left-0 z-20 bg-slate-800 whitespace-nowrap">Total</td>
                            {pivotData.columns.map(col => {
                              const colTotal = pivotData.rows.reduce((sum, row) => 
                                sum + (pivotData.data[row]?.[col] || 0), 0);
                              return (
                                <td key={col} className="border border-slate-700 px-3 py-2 text-right font-bold text-slate-200 whitespace-nowrap">
                                  {formatCurrency(colTotal)}
                                </td>
                              );
                            })}
                            <td className="border border-slate-700 px-3 py-2 text-right font-bold text-amber-400 whitespace-nowrap">
                              {formatCurrency(
                                pivotData.rows.reduce((total, row) => 
                                  total + pivotData.columns.reduce((sum, col) => 
                                    sum + (pivotData.data[row]?.[col] || 0), 0), 0)
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Table className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Click Generate to create pivot table</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default PartnerMFBrokerage;
